/**
 * @zendir/ui — CategoryPalette
 *
 * A lightweight utility that maps category ids to stable colors and labels.
 * Designed for dashboards where multiple components (Timeline, Map, Charts,
 * DataTable) should share a consistent visual identity for the same
 * grouping dimension — teams, squads, subsystems, mission phases, etc.
 *
 * @example
 * ```ts
 * import { CategoryPalette } from '@zendir/ui';
 *
 * const palette = new CategoryPalette([
 *   { id: 'alpha', label: 'Team Alpha', color: '#2dccff' },
 *   { id: 'bravo', label: 'Team Bravo', color: '#fce83a' },
 * ]);
 *
 * palette.getColor('alpha');       // '#2dccff'
 * palette.getLabel('alpha');       // 'Team Alpha'
 * palette.getColor('unknown-id');  // auto-assigned from fallback palette
 * ```
 */

import type { CategoryDef } from "../types";

/**
 * Default fallback palette — Astro-compliant data-viz colors.
 * Used when a category has no explicit color or when an unknown id is
 * encountered at runtime (auto-assignment by insertion order).
 */
const DEFAULT_FALLBACK_PALETTE: readonly string[] = [
  "#3E3CFF", // Zendir Electric
  "#9D70FF", // Zendir Purple
  "#6B69FF", // Electric 20 % lighter
  "#B48DFF", // Purple 20 % lighter
  "#3548C0", // Prussian Blue lighter
  "#9997FF", // Electric 35 % lighter
  "#C9A8FF", // Purple 35 % lighter
  "#1B2DA0", // Prussian Blue
  "#3230CC", // Electric 20 % darker
  "#7E5ACC", // Purple 20 % darker
  "#4A5FD6", // Prussian Blue lightest
];

export class CategoryPalette {
  /** Registered categories (insertion order) */
  private readonly _defs: Map<string, CategoryDef>;
  /** Auto-assigned colors for ids not in the initial set */
  private readonly _autoColors: Map<string, string>;
  /** Fallback palette used for auto-assignment */
  private readonly _fallback: readonly string[];
  /** Counter for round-robin auto-assignment */
  private _autoIndex: number;

  /**
   * @param categories - Initial category definitions (id, label, color).
   * @param fallbackPalette - Optional custom palette for auto-assigned unknowns.
   *   Defaults to the Astro UXDS–compliant Zendir data-viz palette.
   */
  constructor(categories: CategoryDef[] = [], fallbackPalette?: string[]) {
    this._defs = new Map();
    this._autoColors = new Map();
    this._fallback = fallbackPalette ?? DEFAULT_FALLBACK_PALETTE;
    this._autoIndex = 0;

    for (const cat of categories) {
      this._defs.set(String(cat.id), cat);
    }
  }

  /** All registered categories (in insertion order). */
  get all(): CategoryDef[] {
    return [...this._defs.values()];
  }

  /** Number of registered categories. */
  get size(): number {
    return this._defs.size;
  }

  /** Returns the full definition for a registered id, or `undefined`. */
  getCategory(id: string | number): CategoryDef | undefined {
    return this._defs.get(String(id));
  }

  /** Returns `true` if the id was registered in the constructor. */
  has(id: string | number): boolean {
    return this._defs.has(String(id));
  }

  /**
   * Resolve a color for any id.
   * - Registered ids → their explicit `color`.
   * - Unknown ids → stable auto-assigned color from the fallback palette
   *   (same id always gets the same color within this palette instance).
   */
  getColor(id: string | number): string {
    const key = String(id);
    const def = this._defs.get(key);
    if (def) return def.color;

    let auto = this._autoColors.get(key);
    if (!auto) {
      auto = this._fallback[this._autoIndex % this._fallback.length];
      this._autoColors.set(key, auto);
      this._autoIndex++;
    }
    return auto;
  }

  /**
   * Resolve a label for any id.
   * Registered ids → their `label`; unknown ids → the stringified id.
   */
  getLabel(id: string | number): string {
    return this._defs.get(String(id))?.label ?? String(id);
  }

  /**
   * Convert a `CategoryDef` array to a palette instance.
   * Convenience factory — identical to `new CategoryPalette(defs)`.
   */
  static from(
    defs: CategoryDef[],
    fallbackPalette?: string[],
  ): CategoryPalette {
    return new CategoryPalette(defs, fallbackPalette);
  }
}
